"use client";
import * as React from "react";
import { BaseAnchor } from "./base-anchor";
import { isNextClient } from "@perk/core/lib/is-next-client";

export type UniversalHref =
  | string
  | {
      pathname: string;
      // Possibly more fields, e.g. `search: Record<string, unknown>`
      search?: string; // or an object if you prefer
    };

/**
 * External props: unify on `href`.
 * We omit `href` from the anchor's standard attributes so we can define
 * it ourselves as required and typed.
 */
export interface CustomLinkProps
  extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  href: UniversalHref;
  children?: React.ReactNode;
  scrollToTop?: boolean;
}

/**
 * `CustomLink` dynamically imports either:
 * - Next's Link (if we detect Next on the client)
 * - or "createLink" from @tanstack/react-router to build a TanStackLink at runtime.
 *
 * It falls back to a plain <a> if neither is loaded yet (SSR or loading).
 */
export const CustomLink = React.forwardRef<HTMLAnchorElement, CustomLinkProps>(
  function CustomLinkImpl(
    { href, children, scrollToTop = true, ...rest },
    ref
  ) {
    const [LinkImpl, setLinkImpl] =
      React.useState<React.ComponentType<unknown> | null>(null);

    // Function to handle scrolling to top of container or page
    const scrollToTopHandler = React.useCallback(() => {
      if (scrollToTop) {
        // Try to find the perk-widget container
        const perkWidget = document.getElementById("perk-widget");
        if (perkWidget) {
          // If the widget exists, scroll to the top of it
          perkWidget.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          // If no widget container found, scroll to top of page
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }
    }, [scrollToTop]);

    React.useEffect(() => {
      async function loadLink() {
        if (isNextClient()) {
          // Dynamically import next/link
          const mod = await import("next/link");
          // @ts-expect-error custom link
          setLinkImpl(() => mod.default);
        } else {
          // Dynamically import @tanstack/router
          const mod = await import("@tanstack/react-router");
          // We'll need to create the link at runtime
          const { Link: TanStackLinkC } = mod;

          // @ts-expect-error custom link
          setLinkImpl(() => TanStackLinkC);
        }
      }
      loadLink().catch((err) => console.error("Failed to load link:", err));
    }, []);

    // 1) Fallback <a> for SSR or while loading
    if (!LinkImpl) {
      const url = typeof href === "string" ? href : href.pathname;
      return (
        <a ref={ref} href={url} onClick={scrollToTopHandler} {...rest}>
          {children}
        </a>
      );
    }

    // If Next environment => LinkImpl is next/link
    if (isNextClient()) {
      const NextLink = LinkImpl;
      // Transform `href` object to { pathname, query }
      let nextHref:
        | string
        | { pathname: string; search?: Record<string, unknown> };
      if (typeof href === "string") {
        nextHref = href;
      } else {
        nextHref = {
          pathname: `/${href.pathname.replace(/^\/+/, "")}`, // ensure it starts with '/'
        };
        // If you want to map `search` to a query param name, e.g. `ref`
        if (href.search) {
          // @ts-expect-error test
          nextHref.search = href.search;
        }
      }

      return (
        // @ts-expect-error custom link
        <NextLink
          href={nextHref}
          {...rest}
          ref={ref}
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
            if (rest.onClick) {
              rest.onClick(e);
            }
            scrollToTopHandler();
          }}
        >
          {children}
        </NextLink>
      );
    }

    // Otherwise => LinkImpl is TanStack's createLink
    const TanStackLink = LinkImpl;
    // Transform the universal href object to { to, search }
    let toPath: string;
    let searchObj: Record<string, unknown> = {};

    if (typeof href === "string") {
      toPath = href;
    } else {
      toPath = href.pathname;

      if (href.search) {
        // Use Array.from instead of while loop
        searchObj = {};
        const searchParams = new URLSearchParams(href.search);
        for (const [key, value] of searchParams?.entries() ?? []) {
          searchObj[key] = value;
        }
      }
    }

    return (
      // @ts-expect-error custom link
      <TanStackLink
        to={toPath}
        search={
          searchObj
            ? (prev: Record<string, unknown>) => ({ ...prev, ...searchObj })
            : undefined
        }
        {...rest}
        ref={ref}
        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
          if (rest.onClick) {
            rest.onClick(e);
          }
          scrollToTopHandler();
        }}
      >
        {children}
      </TanStackLink>
    );
  }
);

CustomLink.displayName = "CustomLink";
